import React, { useState, useEffect, useMemo } from 'react';
import {
  FaCompass,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaArrowRight,
  FaSpinner,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
} from 'react-icons/fa';
import { getConfiguredItineraries } from '../api/client';

const REGION_COLORS = {
  Europe: 'from-blue-500 to-indigo-600',
  Asia: 'from-red-500 to-pink-600',
  Americas: 'from-emerald-500 to-teal-600',
  Africa: 'from-amber-500 to-orange-600',
  Oceania: 'from-cyan-500 to-sky-600',
  'Middle East': 'from-purple-500 to-fuchsia-600',
};

const CARD_FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';

const ConfiguredItineraryCard = ({ item, onSelect }) => {
  const [imgError, setImgError] = useState(false);
  const regionGradient = REGION_COLORS[item.region] || 'from-neutral-500 to-neutral-600';

  return (
    <button
      type="button"
      onClick={() => item.available && onSelect(item.slug)}
      disabled={!item.available}
      className={`group relative bg-white rounded-2xl shadow-medium hover:shadow-elevated transition-all duration-300 overflow-hidden text-left flex flex-col w-full ${
        item.available ? 'hover:-translate-y-1 cursor-pointer' : 'cursor-not-allowed opacity-70'
      }`}
    >
      {/* Hero image */}
      <div className="relative w-full h-48 overflow-hidden bg-neutral-200">
        <img
          src={imgError ? CARD_FALLBACK_IMAGE : (item.image_url || CARD_FALLBACK_IMAGE)}
          alt={item.display_name}
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Region badge */}
        {item.region && (
          <div
            className={`absolute top-3 left-3 px-3 py-1 rounded-full bg-gradient-to-r ${regionGradient} text-white text-xs font-semibold shadow-md`}
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
          >
            {item.region}
          </div>
        )}
        {/* Availability badge */}
        {!item.available && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-amber-500/95 text-white text-xs font-semibold shadow-md flex items-center gap-1">
            <FaClock size={10} />
            Coming soon
          </div>
        )}
        {item.available && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-emerald-500/95 text-white text-xs font-semibold shadow-md flex items-center gap-1">
            <FaCheckCircle size={10} />
            Ready
          </div>
        )}
        {/* Subtle gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      </div>

      {/* Card body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-bold text-neutral-800 mb-1 line-clamp-2">
          {item.display_name}
        </h3>

        <div className="flex items-center gap-3 text-xs text-neutral-500 mb-2">
          {item.category && (
            <span className="inline-flex items-center gap-1">
              <FaMapMarkerAlt className="text-primary-500" />
              {item.category}
            </span>
          )}
          {item.day_count && (
            <span className="inline-flex items-center gap-1">
              <FaCalendarAlt className="text-primary-500" />
              {item.day_count} days
            </span>
          )}
        </div>

        {item.short_description && (
          <p className="text-sm text-neutral-600 leading-relaxed mb-4 line-clamp-3">
            {item.short_description}
          </p>
        )}

        <div className="mt-auto pt-2 flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
              item.available
                ? 'text-primary-600 group-hover:text-primary-700'
                : 'text-neutral-400'
            }`}
          >
            {item.available ? 'View itinerary' : 'Generating soon'}
            {item.available && <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />}
          </span>
        </div>
      </div>
    </button>
  );
};

const ConfiguredItineraries = ({ onSelectItinerary, onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeRegion, setActiveRegion] = useState('All');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getConfiguredItineraries();
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Failed to load configured itineraries.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const regions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => i.region && set.add(i.region));
    return ['All', ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    if (activeRegion === 'All') return items;
    return items.filter((i) => i.region === activeRegion);
  }, [items, activeRegion]);

  const availableCount = items.filter((i) => i.available).length;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 via-primary-500 to-secondary-500 rounded-2xl p-6 md:p-8 mb-6 text-white shadow-elevated">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 flex items-center gap-3">
              <FaCompass className="text-3xl" />
              Configured Travel Itineraries
            </h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl">
              Hand-curated, AI-generated itineraries to the world's most beautiful places. Browse, explore, and clone any of them to start your own trip — fully customizable after cloning.
            </p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2"
            >
              <span>←</span>
              <span>Back to Dashboard</span>
            </button>
          )}
        </div>
        {items.length > 0 && (
          <div className="mt-4 text-sm text-white/80">
            <span className="font-semibold">{availableCount}</span> of {items.length} itineraries ready to view
            {availableCount < items.length && ' — more being generated'}
          </div>
        )}
      </div>

      {/* Region filter */}
      {regions.length > 2 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {regions.map((region) => (
            <button
              key={region}
              onClick={() => setActiveRegion(region)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeRegion === region
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
              }`}
            >
              {region}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-neutral-500">
          <FaSpinner className="text-4xl text-primary-500 animate-spin mb-3" />
          <p className="text-sm">Loading configured itineraries…</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <FaExclamationTriangle className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-semibold">Couldn't load itineraries</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-20 text-neutral-500">
          <FaCompass className="text-4xl mx-auto mb-3 text-neutral-300" />
          <p className="text-sm">No itineraries available for this filter.</p>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item) => (
            <ConfiguredItineraryCard
              key={item.slug}
              item={item}
              onSelect={onSelectItinerary}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConfiguredItineraries;
